import { money } from "../utils/formatters";
import ClientesList from "./ClientesList";
import PerfilTarifasBadge from "./PerfilTarifasBadge";
import CargosRecurrentesCard from "./CargosRecurrentesCard";


/**
 * Vista detallada para modo admin con todas las métricas financieras
 */
export default function EmpresaAdminView({
    resumen,
    fechaCorte,
    canSeeTarifas,
    clientesOpen = {},
    troncales = {},
    onToggleCliente,
}) {
    return (
        <>
            {/* Grid de métricas principales */}
            <div className="grid md:grid-cols-4 gap-2 p-2 bg-gradient-to-br from-slate-50 to-blue-50">
                {/* Tarjeta de Crédito */}
                <div className="rounded-md border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-2 hover:shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-base">💳</span>
                        <p className="text-[10px] font-bold uppercase text-green-700 tracking-wider">CRÉDITO</p>
                    </div>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between items-center">
                            <span className="text-green-600">Límite de crédito</span>
                            <strong className="text-green-800">{money(resumen.base_limit)}</strong>
                        </div>

                        <div className="flex justify-between items-center">
                            <span className="text-rose-600">Crédito usado</span>
                            <strong className="text-rose-700">{money(resumen.credit_used)}</strong>
                        </div>

                        {resumen.temp_extra > 0 && (
                            <div className="flex justify-between items-center py-1 bg-indigo-50 px-2 rounded-lg">
                                <span className="flex items-center gap-1 text-indigo-600 text-xs">
                                    Extra temporal
                                    <span className="px-1.5 py-0.5 text-xs rounded-full bg-indigo-200 text-indigo-800 font-medium">
                                        ✨
                                    </span>
                                </span>
                                <strong className="text-indigo-800">{money(resumen.temp_extra)}</strong>
                            </div>
                        )}

                        <div className="flex justify-between items-center pt-1 border-t border-green-200">
                            <span
                                className={`font-medium ${resumen.current_credit_limit >= 0 ? "text-green-700" : "text-red-700"
                                    }`}
                            >
                                Crédito disponible
                            </span>
                            <strong
                                className={`text-sm ${resumen.current_credit_limit >= 0 ? "text-green-800" : "text-red-800"
                                    }`}
                                title={
                                    resumen.current_credit_limit < 0
                                        ? "Sobregiro: el crédito disponible es negativo"
                                        : ""
                                }
                            >
                                {money(resumen.current_credit_limit)}
                            </strong>
                        </div>
                        {resumen.current_credit_limit < 0 && (
                            <div className="mt-1 text-[11px] text-red-600 bg-red-100 px-2 py-1 rounded inline-flex items-center gap-1">
                                <span>⚠️</span>
                                <span>Sobregiro (crédito disponible negativo)</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tarjeta de Débito */}
                <div className="rounded-md border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 p-2 hover:shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-base">💰</span>
                        <p className="text-[10px] font-bold uppercase text-blue-700 tracking-wider">DÉBITO</p>
                    </div>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between items-center">
                            <span className="text-blue-600">Depósitos totales</span>
                            <strong className="text-blue-800">
                                {money(resumen.deposito_total ?? resumen.debito_total)}
                            </strong>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-blue-600">Cargos</span>
                            <strong className="text-blue-800">{money(resumen.cargos_total)}</strong>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-blue-600">Depósito disponible</span>
                            <strong className="text-blue-800">
                                {money(
                                    Math.max(
                                        0,
                                        resumen.balance_deposito ??
                                        (resumen.deposito_total ?? resumen.debito_total ?? 0) -
                                        (resumen.cargos_total ?? 0)
                                    )
                                )}
                            </strong>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-blue-600">Promocional</span>
                            <strong className="text-blue-800">{money(resumen.promo_extra)}</strong>
                        </div>
                        <div className="text-[11px] text-blue-500 italic bg-blue-50 px-2 py-1 rounded">
                            (Promo y depósito disponible solo se usan para el saldo)
                        </div>
                    </div>
                </div>

                {/* Tarjeta de Balance */}
                <div
                    className={`rounded-md border-2 p-2 hover:shadow-sm ${resumen.balance >= 0
                        ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50"
                        : "border-red-200 bg-gradient-to-br from-red-50 to-pink-50"
                        }`}
                >
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-base">{resumen.balance >= 0 ? "📈" : "📉"}</span>
                        <p className="text-[10px] font-bold uppercase text-blue-700 tracking-wider">Balance</p>
                        <span className="ml-1 text-[10px] text-slate-500 italic normal-case">al {fechaCorte}</span>
                    </div>

                    <div className={`text-xl font-bold mb-1 ${resumen.balance >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                        {money(resumen.balance)}
                    </div>
                    <p className="text-[11px] text-slate-600 leading-relaxed bg-white/60 px-2 py-1 rounded">
                        = Crédito disponible + <span className="font-medium">Depósito disponible</span> + Promo
                    </p>
                    {resumen.balance < 0 && (
                        <div className="mt-2 text-[11px] text-red-600 bg-red-100 px-2 py-1 rounded flex items-center gap-1">
                            {/* <span>⚠️</span>
                            <span>Nos debe (sin depósito y con crédito sobregirado)</span> */}
                        </div>
                    )}
                </div>

                {/* Tarjeta de Tarifas */}
                {canSeeTarifas && (
                    <div className="rounded-md border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-2 hover:shadow-sm">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-base">💲</span>
                            <p className="text-[10px] font-bold uppercase text-violet-700 tracking-wider">Tarifas</p>
                        </div>

                        <PerfilTarifasBadge
                            perfilNombre={resumen?.perfil_nombre || null}
                            tarifas={resumen?.tarifas}
                        />
                    </div>
                )}
            </div>

            {/* ✨ NUEVO: Segunda fila con Cargos Recurrentes */}
            <div className="grid md:grid-cols-4 gap-2 px-2 pb-2">
                <div className="md:col-span-1">
                    <CargosRecurrentesCard empresaId={resumen?.id || resumen?.empresa_id} />
                </div>

                {/* Espacio para más tarjetas si las necesitas */}
                <div className="md:col-span-3">
                    {/* Puedes agregar más información aquí */}
                </div>
            </div>

            {/* Lista de clientes y troncales */}
            <ClientesList
                clientes={resumen.clientes || []}
                troncales={troncales}
                clientesOpen={clientesOpen}
                onToggleCliente={onToggleCliente}
            />
        </>
    );
}